import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  Question,
  QuestionInputTypeComponent,
  QuestionOption,
  UpdateAnswer,
} from '@features/questionnaire/interfaces/questionnaire.interface';
import { BehaviorSubject, debounceTime, Subject, takeUntil } from 'rxjs';

interface MultipleChoiceSingleAnswer {
  option_id: string;
  answer_text: string;
}

@Component({
  selector: 'app-multiple-choice-single-input-type',
  templateUrl: './multiple-choice-single-input-type.component.html',
  styleUrls: ['./multiple-choice-single-input-type.component.scss'],
})
export class QuestionInputTypeMultipleChoiceSingleComponent
  implements QuestionInputTypeComponent<MultipleChoiceSingleAnswer>, OnInit, OnDestroy
{
  @Output()
  public updateAnswer: EventEmitter<UpdateAnswer> = new EventEmitter<UpdateAnswer>();

  public formGroup: UntypedFormGroup;
  public options: QuestionOption[];
  public answer$: BehaviorSubject<MultipleChoiceSingleAnswer> = new BehaviorSubject<MultipleChoiceSingleAnswer>(null);
  private destroy$: Subject<boolean> = new Subject<boolean>();

  private _question: Question;

  @Input()
  public set question(question: Question) {
    if (question) {
      this._question = question;
      this.options = question.question_options;
    }
  }

  public ngOnInit(): void {
    this.formGroup = this.generateFormGroup();

    this.formGroup.valueChanges
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((value: MultipleChoiceSingleAnswer) => {
        const updatedValue = {
          ...value,
          answer_text: this.options.find((option) => {
            return option.id === value.option_id;
          })?.is_free_text
            ? value.answer_text
            : null,
        };

        this.updateAnswer.emit({
          id: this._question.id,
          value: updatedValue,
        });
      });

    this.answer$
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: MultipleChoiceSingleAnswer) => {
        if (value) {
          this.formGroup.patchValue(value);
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public validate(): boolean {
    if (this.formGroup.valid) {
      this.updateAnswer.emit({
        id: this._question.id,
        value: this.formGroup.value,
      });
    }

    return this.formGroup.valid;
  }

  public getAnswer(): MultipleChoiceSingleAnswer {
    return this.formGroup.value;
  }

  private generateFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      option_id: this._question.is_required
        ? new UntypedFormControl(null, Validators.required)
        : new UntypedFormControl(null),
      answer_text: new UntypedFormControl(null),
    });
  }
}
